import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Flower2, Sparkles, Zap, Award, TrendingUp, Share2, Menu, X, Trophy, User, Bell, CloudOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import BottomNav from '../components/BottomNav';
import FleurDeVie from '../components/FleurDeVie';
import SocialShare from '../components/SocialShare';
import { useAuth } from '../context/AuthContext';
import { useOffline } from '../context/OfflineContext';
import { useNotifications } from '../context/NotificationContext';
import { userAPI, contraceptionAPI, seedAPI } from '../services/api';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, logout, updateUser, loading: authLoading } = useAuth();
  const { isOnline, cacheData, getCachedData, queueAction } = useOffline();
  const { unreadCount } = useNotifications();
  const [user, setUser] = useState(null);
  const [contraception, setContraception] = useState(null);
  const [badges, setBadges] = useState([]);
  const [checkedIn, setCheckedIn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isStaleData, setIsStaleData] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Seed data on first load
    const seedDataOnce = async () => {
      try {
        await seedAPI.seed();
      } catch (error) {
        console.log('Seed already done or failed:', error);
      }
    };

    const fetchData = async () => {
      // Try cached data first
      const cachedProfile = await getCachedData('/api/users/profile');
      if (cachedProfile) {
        const cachedData = cachedProfile.data?.user || cachedProfile.data;
        setUser(cachedData);
        setIsStaleData(cachedProfile.isStale);
        setLoading(false);
      }

      if (!isOnline && cachedProfile) {
        // Offline with cache - use cached data
        return;
      }

      try {
        const profileRes = await userAPI.getProfile();
        const profileData = profileRes.data.user || profileRes.data;
        setUser(profileData);
        updateUser(profileData);
        cacheData('/api/users/profile', profileData);
        setIsStaleData(false);

        const contraceptionRes = await contraceptionAPI.get();
        setContraception(contraceptionRes.data.contraception);

        try {
          const badgesRes = await api.post('/users/check-badges');
          setBadges(badgesRes.data.badges || []);
        } catch (e) {
          console.log('Badges check failed:', e);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (!cachedProfile) {
          toast({
            title: 'Erreur',
            description: isOnline ? 'Impossible de charger les données' : 'Aucune donnée en cache',
            variant: 'destructive'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOnline) seedDataOnce();
    fetchData();
  }, [isAuthenticated, navigate, authLoading]);

  const handleShareApp = async () => {
    const shareMessage = "Je progresse sur Fleur de Vie ! Rejoins-moi sur cette super plateforme pour apprendre et prendre soin de ta santé. C'est gratuit et sécurisé !";
    const shareUrl = "https://fleurde-vie.com";

    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: 'Fleur de Vie',
          text: shareMessage,
          url: shareUrl,
          dialogTitle: 'Partager Fleur de Vie',
        });
      } else if (navigator.share) {
        await navigator.share({
          title: 'Fleur de Vie',
          text: shareMessage,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(`${shareMessage}\n${shareUrl}`);
        toast({ title: 'Lien copié !', description: 'Le message a été copié dans le presse-papier' });
      }
      // Record share for points
      try {
        await api.post('/user/record-share');
        toast({ title: '+5 points !', description: 'Merci pour le partage !' });
      } catch (e) { console.warn('[Dashboard] Record share failed:', e.message); }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
      }
    }
  };

  const handleCheckIn = async () => {
    if (!isOnline) {
      // Queue check-in for when we're back online
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      await queueAction({
        type: 'checkin',
        url: `${baseUrl}/api/users/checkin`,
        method: 'POST',
      });
      setCheckedIn(true);
      // Optimistic update
      setUser(prev => ({
        ...prev,
        points: (prev?.points || 0) + 10,
        currentStreak: (prev?.currentStreak || 0) + 1
      }));
      toast({
        title: 'Check-in enregistré',
        description: 'Sera synchronisé quand la connexion reviendra'
      });
      return;
    }

    try {
      const response = await userAPI.checkIn();
      setCheckedIn(true);
      const newPoints = response.data.totalPoints;
      const newStreak = response.data.streak;
      const newLevel = response.data.level;
      setUser(prev => ({
        ...prev,
        points: newPoints,
        currentStreak: newStreak,
        level: newLevel
      }));
      updateUser({
        ...user,
        points: newPoints,
        currentStreak: newStreak,
        level: newLevel
      });
      toast({
        title: 'Check-in réussi!',
        description: `+${response.data.pointsEarned} points! Série: ${newStreak} jour(s)`
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Check-in échoué';
      if (errorMessage.includes('déjà réclamé') || errorMessage.includes('Already')) {
        setCheckedIn(true);
        toast({
          title: 'Information',
          description: errorMessage,
        });
      } else {
        toast({
          title: 'Erreur',
          description: errorMessage,
          variant: 'destructive'
        });
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const progressPercent = (user.points % 1000) / 10;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Offline Banner */}
      {(isStaleData || !isOnline) && (
        <div className="bg-orange-600/90 px-4 py-2 flex items-center justify-center gap-2 text-white text-sm" data-testid="dashboard-offline-banner">
          <CloudOff className="w-4 h-4" />
          <span>{!isOnline ? 'Mode hors ligne' : 'Données en cache'}</span>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 flex justify-between items-center z-40">
        <Flower2 className="w-10 h-10 text-pink-300" />
        <button 
          onClick={() => setMenuOpen(!menuOpen)}
          className="text-white p-2"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Dropdown Menu */}
      {menuOpen && (
        <div className="fixed top-16 right-4 bg-purple-900/95 backdrop-blur-md border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden">
          <button
            onClick={() => {
              setMenuOpen(false);
              navigate('/profile');
            }}
            className="block w-full text-left px-6 py-3 text-white hover:bg-white/10 transition-colors"
          >
            Mon Profil
          </button>
          <button
            onClick={() => {
              setMenuOpen(false);
              navigate('/notifications');
            }}
            className="block w-full text-left px-6 py-3 text-white hover:bg-white/10 transition-colors border-t border-white/10"
          >
            Notifications
          </button>
          {user?.role === 'super_admin' && (
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate('/push-test');
              }}
              className="block w-full text-left px-6 py-3 text-white hover:bg-white/10 transition-colors border-t border-white/10"
            >
              Test Push
            </button>
          )}
          <button
            onClick={() => {
              setMenuOpen(false);
              handleLogout();
            }}
            className="block w-full text-left px-6 py-3 text-pink-300 hover:bg-white/10 transition-colors border-t border-white/10"
          >
            Déconnexion
          </button>
        </div>
      )}

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Welcome Section */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Bonjour, <span className="text-pink-300">{user.firstName || 'Utilisateur'}</span> !
            </h1>
            <p className="text-pink-200">Prete a prendre soin de vous aujourd'hui ?</p>
          </div>
          <button onClick={() => navigate('/notifications')} className="relative p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors" data-testid="notif-bell-btn">
            <Bell className="w-6 h-6 text-pink-200" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notif-bell-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* User Level Card with Profile Link */}
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
          <Link to="/profile" className="flex items-center gap-4 mb-4" data-testid="profile-link">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-pink-400 relative">
              {user.avatar ? (
                <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-2xl font-bold">
                  {(user.firstName || '').charAt(0)}{(user.lastName || '').charAt(0)}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center text-xs text-white">
                {user.level}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <span className="text-white font-semibold">Niveau {user.level}</span>
                <Zap className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-semibold">{user.points} pts</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-purple-900/50" />
            </div>
          </Link>
        </Card>

        {/* Check-in Card */}
        <Card className="p-6 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-md border-yellow-400/30">
          <div className="flex items-start gap-3 mb-4">
            <Zap className="w-8 h-8 text-yellow-400" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white mb-2">Check-in Quotidien</h3>
              <p className="text-pink-100 text-sm mb-2">
                Revenez chaque jour pour gagner des points et maintenir votre série !
              </p>
              <div className="text-yellow-300 font-bold text-lg">
                Série actuelle : {user.currentStreak} jour{user.currentStreak > 1 ? 's' : ''} 🔥
              </div>
            </div>
          </div>
          <Button
            onClick={handleCheckIn}
            disabled={checkedIn}
            className="w-full bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50"
          >
            {checkedIn ? '✓ Points réclamés !' : 'Réclamer mes points'}
          </Button>
        </Card>

        {/* Fleur de Vie - Sacred Geometry Gamification */}
        <Card className="p-6 bg-gradient-to-br from-purple-600/30 to-pink-600/30 backdrop-blur-md border-purple-400/30">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-6 h-6 text-yellow-400" />
            <h3 className="text-xl font-bold text-white">Votre Fleur de Vie</h3>
          </div>
          <div className="flex justify-center">
            <FleurDeVie 
              points={user.points || 0} 
              level={user.level || 1} 
              size={180}
              showStats={true}
              animated={true}
            />
          </div>
          <p className="text-center text-pink-200 text-sm mt-4">
            Complétez des leçons et des quiz pour faire grandir votre Fleur de Vie!
          </p>
        </Card>

        {/* Leaderboard Link */}
        <Card className="p-4 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-md border-yellow-400/30">
          <Link to="/leaderboard" className="flex items-center justify-between" data-testid="leaderboard-link">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Classement</h4>
                <p className="text-yellow-100 text-xs">Voir le Top 20 élèves et écoles</p>
              </div>
            </div>
            <div className="text-yellow-400 text-2xl">→</div>
          </Link>
        </Card>

        {/* Girls Room Leaderboard */}
        <Card className="p-4 bg-gradient-to-br from-pink-500/20 to-purple-500/20 backdrop-blur-md border-pink-400/30">
          <Link to="/girls-room-leaderboard" className="flex items-center justify-between" data-testid="girls-room-leaderboard-link">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-400/20 flex items-center justify-center">
                <span className="text-2xl">🌸</span>
              </div>
              <div>
                <h4 className="text-white font-semibold">Classement Girls Room</h4>
                <p className="text-pink-100 text-xs">Par ville et session</p>
              </div>
            </div>
            <div className="text-pink-400 text-2xl">→</div>
          </Link>
        </Card>

        {/* Custom Alerts */}
        <Card className="p-4 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-md border-blue-400/30">
          <Link to="/custom-alerts" className="flex items-center justify-between" data-testid="custom-alerts-link">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-400/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Alertes Personnalisées</h4>
                <p className="text-blue-100 text-xs">Règles, ovulation, contraception</p>
              </div>
            </div>
            <div className="text-blue-400 text-2xl">→</div>
          </Link>
        </Card>

        {/* Share Card */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="w-5 h-5 text-pink-300" />
            <h4 className="text-white font-semibold">Partagez avec vos amies</h4>
          </div>
          <SocialShare compact className="justify-center" />
        </Card>

        {/* Badges */}
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-white">Mes Badges</h3>
            <Link to="/profile" className="text-pink-300 text-sm hover:text-white">
              Voir tout →
            </Link>
          </div>
          {badges.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {badges.slice(0, 6).map((badge, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-400/30"
                  title={badge.description}
                >
                  <span className="text-lg">{badge.icon}</span>
                  <span className="text-white text-xs font-medium">{badge.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-pink-200 text-sm">
              Complétez des modules pour gagner vos premiers badges!
            </p>
          )}
        </Card>

        {/* Weekly Challenges */}
        <Link to="/challenges">
          <Card className="p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-400/30 hover:from-yellow-500/30 hover:to-orange-500/30 transition-all cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/30 rounded-full">
                  <Trophy className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold">Défis de la Semaine</h4>
                  <p className="text-pink-200 text-sm">Gagne des points et des récompenses!</p>
                </div>
              </div>
              <div className="text-yellow-300 font-bold text-lg">→</div>
            </div>
          </Card>
        </Link>

        {/* Contraception Card */}
        {contraception && (
          <>
            <div className="flex gap-2 mb-4">
              <button className="px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm">
                📅 Cycle
              </button>
              <button className="px-4 py-2 rounded-full bg-pink-500/30 backdrop-blur-sm border border-pink-400/50 text-white text-sm font-semibold">
                🛡️ Contraception
              </button>
              <button className="px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm">
                🤰 Grossesse
              </button>
            </div>

            <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-white">Ma Contraception</h3>
                <button className="text-pink-300 text-sm flex items-center gap-1">
                  ✏️ Modifier
                </button>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  <span className="text-3xl">🛡️</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold text-lg">{contraception.type}</h4>
                  <p className="text-pink-200 text-sm">Prise quotidienne à {contraception.time}</p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-pink-100 text-sm">
                  📅 <span>Renouvellement le: {contraception.renewalDate}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-pink-200 text-sm">Temps restant</span>
                  <span className="text-yellow-300 font-bold">{contraception.daysRemaining} jours</span>
                </div>
                <Progress value={(contraception.daysRemaining / 90) * 100} className="h-2 bg-purple-900/50" />
              </div>

              <Button className="w-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white py-3 rounded-xl mb-4">
                🔔 Gérer les rappels
              </Button>
            </Card>
          </>
        )}

        {/* Share Card */}
        <Card className="p-6 bg-gradient-to-br from-pink-500/20 to-purple-500/20 backdrop-blur-md border-pink-400/30">
          <div className="flex items-start gap-3">
            <Share2 className="w-8 h-8 text-pink-300" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white mb-2">Partagez & Gagnez des Points</h3>
              <p className="text-pink-100 text-sm mb-4">
                "Je progresse sur Fleur de Vie ! Rejoins-moi sur cette super plateforme pour apprendre et prendre soin de ta santé. C'est gratuit et sécurisé !"
              </p>
              <Button 
                onClick={handleShareApp}
                className="w-full bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white font-semibold py-3 rounded-xl"
                data-testid="share-now-btn"
              >
                Partager maintenant
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <BottomNav active="dashboard" />
    </div>
  );
};

export default Dashboard;
